import { invokeFunction } from "../api";
import { toApiRequestError } from "../errors";

export type ProviderStatus = "active" | "provisional" | "inactive";
export type CredentialType =
  | "medical_license"
  | "dea_registration"
  | "board_certification"
  | "malpractice_insurance"
  | "npdb_query"
  | "oig_sam_check"
  | "bls"
  | "acls"
  | "other";
export type PrivilegeCategory =
  | "anesthesia"
  | "gastroenterology"
  | "ophthalmology"
  | "orthopedic"
  | "pain_management"
  | "general_surgery"
  | "podiatry"
  | "other";
export type ExpiryStatus = "green" | "amber" | "red" | "unknown";
export type CredentialDocumentType =
  | CredentialType
  | "privilege_supporting";

export interface CredentialingProviderSummary {
  provider_status: ExpiryStatus;
  next_expiry_date: string | null;
  credential_status_counts: Record<ExpiryStatus, number>;
  privilege_status_counts: Record<ExpiryStatus, number>;
}

export interface ProviderRecord {
  provider_id: string;
  tenant_id: string;
  facility_id: string;
  name: string;
  npi: string;
  specialty: string;
  status: ProviderStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProviderListItem extends ProviderRecord {
  summary: CredentialingProviderSummary;
}

export interface CredentialRecord {
  credential_id: string;
  tenant_id: string;
  provider_id: string;
  credential_type: CredentialType;
  issuer: string;
  number: string;
  issued_date: string | null;
  expiry_date: string | null;
  verified: boolean;
  verified_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrivilegeRecord {
  privilege_id: string;
  tenant_id: string;
  provider_id: string;
  facility_id: string;
  procedure_category: PrivilegeCategory;
  granted_date: string;
  expiry_date: string | null;
  granted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CredentialingProviderDetailResponse {
  provider: ProviderRecord;
  credentials: CredentialRecord[];
  privileges: PrivilegeRecord[];
  summary: CredentialingProviderSummary;
}

export interface CredentialDocumentRecord {
  document_id: string;
  tenant_id: string;
  provider_id: string;
  credential_id: string | null;
  document_type: CredentialDocumentType | "other";
  label: string;
  file_path: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface CredentialingSummaryResponse {
  total_providers: number;
  status_counts: Record<ExpiryStatus, number>;
  expiring_items: Array<{
    item_type: "credential" | "privilege";
    item_id: string;
    provider_id: string;
    label: string;
    expiry_date: string | null;
    status: ExpiryStatus;
  }>;
}

function asRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asExpiryStatus(value: unknown): ExpiryStatus {
  return value === "green" || value === "amber" || value === "red"
    ? value
    : "unknown";
}

function parseSummary(value: unknown): CredentialingProviderSummary {
  if (!asRecord(value))
    throw new Error("Malformed credentialing summary from server");
  const parseCounts = (counts: unknown): Record<ExpiryStatus, number> => {
    if (!asRecord(counts)) return { green: 0, amber: 0, red: 0, unknown: 0 };
    return {
      green: typeof counts.green === "number" ? counts.green : 0,
      amber: typeof counts.amber === "number" ? counts.amber : 0,
      red: typeof counts.red === "number" ? counts.red : 0,
      unknown: typeof counts.unknown === "number" ? counts.unknown : 0,
    };
  };

  return {
    provider_status: asExpiryStatus(value.provider_status),
    next_expiry_date:
      typeof value.next_expiry_date === "string"
        ? value.next_expiry_date
        : null,
    credential_status_counts: parseCounts(value.credential_status_counts),
    privilege_status_counts: parseCounts(value.privilege_status_counts),
  };
}

function parseProvider(value: unknown): ProviderRecord {
  if (!asRecord(value))
    throw new Error("Malformed provider response from server");
  if (
    typeof value.provider_id !== "string" ||
    typeof value.tenant_id !== "string" ||
    typeof value.facility_id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.npi !== "string" ||
    typeof value.specialty !== "string" ||
    typeof value.status !== "string" ||
    typeof value.created_at !== "string" ||
    typeof value.updated_at !== "string"
  ) {
    throw new Error("Malformed provider response from server");
  }

  return {
    provider_id: value.provider_id,
    tenant_id: value.tenant_id,
    facility_id: value.facility_id,
    name: value.name,
    npi: value.npi,
    specialty: value.specialty,
    status: value.status as ProviderStatus,
    created_by: typeof value.created_by === "string" ? value.created_by : null,
    created_at: value.created_at,
    updated_at: value.updated_at,
  };
}

function parseCredential(value: unknown): CredentialRecord {
  if (!asRecord(value))
    throw new Error("Malformed credential response from server");
  if (
    typeof value.credential_id !== "string" ||
    typeof value.tenant_id !== "string" ||
    typeof value.provider_id !== "string" ||
    typeof value.credential_type !== "string" ||
    typeof value.issuer !== "string" ||
    typeof value.number !== "string" ||
    typeof value.verified !== "boolean" ||
    typeof value.created_at !== "string" ||
    typeof value.updated_at !== "string"
  ) {
    throw new Error("Malformed credential response from server");
  }

  return {
    credential_id: value.credential_id,
    tenant_id: value.tenant_id,
    provider_id: value.provider_id,
    credential_type: value.credential_type as CredentialType,
    issuer: value.issuer,
    number: value.number,
    issued_date:
      typeof value.issued_date === "string" ? value.issued_date : null,
    expiry_date:
      typeof value.expiry_date === "string" ? value.expiry_date : null,
    verified: value.verified,
    verified_date:
      typeof value.verified_date === "string" ? value.verified_date : null,
    created_at: value.created_at,
    updated_at: value.updated_at,
  };
}

function parsePrivilege(value: unknown): PrivilegeRecord {
  if (!asRecord(value))
    throw new Error("Malformed privilege response from server");
  if (
    typeof value.privilege_id !== "string" ||
    typeof value.tenant_id !== "string" ||
    typeof value.provider_id !== "string" ||
    typeof value.facility_id !== "string" ||
    typeof value.procedure_category !== "string" ||
    typeof value.granted_date !== "string" ||
    typeof value.created_at !== "string" ||
    typeof value.updated_at !== "string"
  ) {
    throw new Error("Malformed privilege response from server");
  }

  return {
    privilege_id: value.privilege_id,
    tenant_id: value.tenant_id,
    provider_id: value.provider_id,
    facility_id: value.facility_id,
    procedure_category: value.procedure_category as PrivilegeCategory,
    granted_date: value.granted_date,
    expiry_date:
      typeof value.expiry_date === "string" ? value.expiry_date : null,
    granted_by: typeof value.granted_by === "string" ? value.granted_by : null,
    created_at: value.created_at,
    updated_at: value.updated_at,
  };
}

function parseCredentialDocument(value: unknown): CredentialDocumentRecord {
  if (!asRecord(value)) {
    throw new Error("Malformed credential document response from server");
  }
  if (
    typeof value.document_id !== "string" ||
    typeof value.tenant_id !== "string" ||
    typeof value.provider_id !== "string" ||
    typeof value.document_type !== "string" ||
    typeof value.label !== "string" ||
    typeof value.file_path !== "string" ||
    typeof value.created_at !== "string"
  ) {
    throw new Error("Malformed credential document response from server");
  }
  return {
    document_id: value.document_id,
    tenant_id: value.tenant_id,
    provider_id: value.provider_id,
    credential_id: typeof value.credential_id === "string"
      ? value.credential_id
      : null,
    document_type: value.document_type as CredentialDocumentType | "other",
    label: value.label,
    file_path: value.file_path,
    file_size_bytes: typeof value.file_size_bytes === "number"
      ? value.file_size_bytes
      : null,
    mime_type: typeof value.mime_type === "string" ? value.mime_type : null,
    uploaded_by: typeof value.uploaded_by === "string" ? value.uploaded_by : null,
    created_at: value.created_at,
  };
}

function buildQuery(params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const serialized = query.toString();
  return serialized.length > 0 ? `?${serialized}` : "";
}

export async function getCredentialingSummary(
  facilityId?: string,
): Promise<CredentialingSummaryResponse> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "GET",
    path: `/summary${buildQuery({ facility_id: facilityId })}`,
  });

  if (error)
    throw toApiRequestError(error, "Failed to load credentialing summary");
  if (!asRecord(data) || !asRecord(data.summary))
    throw new Error("Malformed credentialing summary response from server");

  return {
    total_providers:
      typeof data.summary.total_providers === "number"
        ? data.summary.total_providers
        : 0,
    status_counts: asRecord(data.summary.status_counts)
      ? {
          green:
            typeof data.summary.status_counts.green === "number"
              ? data.summary.status_counts.green
              : 0,
          amber:
            typeof data.summary.status_counts.amber === "number"
              ? data.summary.status_counts.amber
              : 0,
          red:
            typeof data.summary.status_counts.red === "number"
              ? data.summary.status_counts.red
              : 0,
          unknown:
            typeof data.summary.status_counts.unknown === "number"
              ? data.summary.status_counts.unknown
              : 0,
        }
      : { green: 0, amber: 0, red: 0, unknown: 0 },
    expiring_items: Array.isArray(data.summary.expiring_items)
      ? data.summary.expiring_items
          .filter(
            (item): item is Record<string, unknown> =>
              asRecord(item) && typeof item.item_id === "string",
          )
          .map((item) => ({
            item_type:
              item.item_type === "privilege" ? "privilege" : "credential",
            item_id: item.item_id as string,
            provider_id:
              typeof item.provider_id === "string" ? item.provider_id : "",
            label: typeof item.label === "string" ? item.label : "",
            expiry_date:
              typeof item.expiry_date === "string" ? item.expiry_date : null,
            status: asExpiryStatus(item.status),
          }))
      : [],
  };
}

export async function getCredentialingProviders(
  facilityId?: string,
): Promise<ProviderListItem[]> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "GET",
    path: `/providers${buildQuery({ facility_id: facilityId })}`,
  });

  if (error) throw toApiRequestError(error, "Failed to load providers");
  if (!asRecord(data) || !Array.isArray(data.providers))
    throw new Error("Malformed providers response from server");

  return data.providers.map((entry) => {
    if (!asRecord(entry))
      throw new Error("Malformed providers response from server");
    return {
      ...parseProvider(entry),
      summary: parseSummary(entry.summary),
    };
  });
}

export interface CreateProviderInput {
  facility_id: string;
  name: string;
  npi: string;
  specialty: string;
  status?: ProviderStatus;
}

export interface UpdateProviderInput {
  facility_id?: string;
  name?: string;
  npi?: string;
  specialty?: string;
  status?: ProviderStatus;
}

/** Admin/superadmin — POST /providers (#1852). */
export async function createCredentialingProvider(
  input: CreateProviderInput,
): Promise<ProviderRecord> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "POST",
    path: "/providers",
    body: {
      facility_id: input.facility_id,
      name: input.name,
      npi: input.npi,
      specialty: input.specialty,
      ...(input.status ? { status: input.status } : {}),
    },
  });

  if (error) throw toApiRequestError(error, "Failed to create provider");
  if (!asRecord(data) || !asRecord(data.provider)) {
    throw new Error("Malformed create provider response from server");
  }
  return parseProvider(data.provider);
}

/** Admin/superadmin — PATCH /providers/:id (#1852). */
export async function updateCredentialingProvider(
  providerId: string,
  input: UpdateProviderInput,
): Promise<ProviderRecord> {
  const body: Record<string, string> = {};
  if (typeof input.facility_id === "string") body.facility_id = input.facility_id;
  if (typeof input.name === "string") body.name = input.name;
  if (typeof input.npi === "string") body.npi = input.npi;
  if (typeof input.specialty === "string") body.specialty = input.specialty;
  if (typeof input.status === "string") body.status = input.status;

  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "PATCH",
    path: `/providers/${providerId}`,
    body,
  });

  if (error) throw toApiRequestError(error, "Failed to update provider");
  if (!asRecord(data) || !asRecord(data.provider)) {
    throw new Error("Malformed update provider response from server");
  }
  return parseProvider(data.provider);
}

export async function getCredentialingProviderDetail(
  providerId: string,
): Promise<CredentialingProviderDetailResponse> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "GET",
    path: `/providers/${providerId}`,
  });

  if (error) throw toApiRequestError(error, "Failed to load provider detail");
  if (!asRecord(data))
    throw new Error("Malformed provider detail response from server");

  return {
    provider: parseProvider(data.provider),
    credentials: Array.isArray(data.credentials)
      ? data.credentials.map(parseCredential)
      : [],
    privileges: Array.isArray(data.privileges)
      ? data.privileges.map(parsePrivilege)
      : [],
    summary: parseSummary(data.summary),
  };
}

export async function getCredentialingDocuments(
  providerId: string,
): Promise<CredentialDocumentRecord[]> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "GET",
    path: `/providers/${providerId}/documents`,
  });

  if (error) {
    throw toApiRequestError(error, "Failed to load credentialing documents");
  }
  if (!asRecord(data) || !Array.isArray(data.documents)) {
    throw new Error("Malformed credentialing documents response from server");
  }
  return data.documents.map(parseCredentialDocument);
}

export async function uploadCredentialDocument(
  providerId: string,
  input: {
    document_type: CredentialDocumentType | "other";
    label: string;
    credential_id?: string;
    file: File;
  },
): Promise<CredentialDocumentRecord> {
  const formData = new FormData();
  formData.append("document_type", input.document_type);
  formData.append("label", input.label);
  if (input.credential_id) formData.append("credential_id", input.credential_id);
  formData.append("file", input.file);

  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "POST",
    path: `/providers/${providerId}/documents/upload`,
    body: formData,
  });

  if (error || !asRecord(data) || !data.document) {
    throw toApiRequestError(error, "Failed to upload credential document");
  }
  return parseCredentialDocument(data.document);
}

export async function emitCredentialingExpiryAlerts(
  providerId: string,
): Promise<{ count: number; status: string }> {
  const { data, error } = await invokeFunction<unknown>("credentialing", {
    method: "POST",
    path: `/providers/${providerId}/expiry-alerts`,
  });

  if (error || !asRecord(data)) {
    throw toApiRequestError(
      error,
      "Failed to send credentialing expiration alerts",
    );
  }
  return {
    count: typeof data.count === "number" ? data.count : 0,
    status: typeof data.status === "string" ? data.status : "ok",
  };
}
